export type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  // Перевод content на русский (обе стороны диалога говорят по-турецки) —
  // подсказка при наведении в ai-chat.tsx. null у сообщений, отправленных до
  // ввода этого поля (старые строки в БД).
  translationRu: string | null
  correction: { corrected: string; explanationRu: string; topic?: string | null } | null
  // Разговор с грамматической целью: употребил ли ученик целевую форму в этой
  // реплике (оценка AI, .scratch/grammar-trainer/06). Только у user-сообщений.
  goalUse?: 'correct' | 'incorrect' | null
  // Только у assistant-сообщений — тап-варианты ответа ученика, собранные
  // строго из его известных слов (см. lib/deepseek.ts). У user всегда [].
  suggestedReplies: SuggestedReply[]
  createdAt: string
}

// Вариант ответа с переводом: новичок видит, что выбирает (10.10.2026).
// ru null у вариантов, сохранённых до перевода (строки в jsonb и кэше).
export type SuggestedReply = { tr: string; ru: string | null }

// Варианты из ответа модели или из БД: старый формат - строки, новый - {tr, ru}.
export function normalizeSuggestions(raw: unknown): SuggestedReply[] {
  if (!Array.isArray(raw)) return []
  const out: SuggestedReply[] = []
  for (const item of raw) {
    if (typeof item === 'string') {
      if (item.trim()) out.push({ tr: item.trim(), ru: null })
    } else if (item && typeof item === 'object' && typeof (item as { tr?: unknown }).tr === 'string') {
      const tr = (item as { tr: string }).tr.trim()
      const ru = (item as { ru?: unknown }).ru
      if (tr) out.push({ tr, ru: typeof ru === 'string' && ru.trim() ? ru.trim() : null })
    }
  }
  return out.slice(0, 3)
}

// Недельные лимиты AI для аккаунтов без подписки (profiles.subscription_status
// = 'free'); неделя — с понедельника по местному времени ученика. Неделя, а не
// сутки (обсуждение тарифов 2026-09-11): можно позаниматься много в выходные,
// но ежедневный «выбор лимита до дна» обходится дешевле. Считаются по журналу
// ai_usage (lib/ai-usage.ts) — там только факт запроса, без текста.
export const FREE_WEEKLY_MESSAGE_LIMIT = 50
// После последнего бесплатного урока курса (FREE_COURSE_LAST_SORT) бесплатный
// разговор сужается до 30 реплик в неделю, без дневного дробления: можно
// потратить за один день (решение пользователя 09.10.2026, тикет 44).
export const FREE_WEEKLY_MESSAGE_LIMIT_AFTER_FREE = 30
// Каждый вопрос — длинный ответ (~700 токенов на выходе).
export const FREE_WEEKLY_QA_LIMIT = 50
// Вопросы о жизни в Турции (не о языке) в «Спроси Turkido»: Plus/Pro — в рамках
// лимита вопросов, Free — два пробных в неделю (решение 2026-09-18). Считается
// только вопрос, который модель отнесла к теме «Турция».
export const FREE_WEEKLY_TURKEY_QA_LIMIT = 2
// «Своя ситуация» (.scratch/custom-scene/01): генерация сцены под запрос
// ученика — длинный ответ модели (роль, цель, 8–10 фраз с переводом). Free —
// две пробные в неделю, Plus ×3, Pro без лимита; повтор готовой сцены
// генерацию не тратит.
export const FREE_WEEKLY_CUSTOM_SCENE_LIMIT = 2
// Первые реплики разговоров не видны ученику как расход, но стоят запроса:
// тихий потолок против «Новый разговор» по кругу. Готовые из кэша не считаются.
export const FREE_WEEKLY_OPENING_LIMIT = 50
// Перевод по тапу: считается только обращение к модели (словарь курса и
// общий кэш — бесплатно; фразы уроков прогреты заранее). Одно обращение
// переводит всё предложение, так что 300 — это сотни новых фраз в неделю.
export const FREE_WEEKLY_TRANSLATE_LIMIT = 300
// Озвучка на лету (Google TTS) — только синтез нового текста; озвучка уроков
// и кэш не считаются. При исчерпании кнопка 🔊 говорит голосом браузера.
// 50, а не больше (решение 2026-09-15): Chirp 3 HD — самая дорогая статья
// (30 $/млн символов), а новых фраз при 50 репликах чата больше и не нужно.
export const FREE_WEEKLY_TTS_LIMIT = 50

// Фото-перевод (.scratch/photo-translate): снимок целиком уходит в DeepSeek,
// около 0,1-0,3 ₽ за снимок; бесплатно несколько на пробу, дальше Plus.
export const FREE_WEEKLY_PHOTO_LIMIT = 3
// Без аккаунта (решение 06.10.2026): столько снимков на устройство за всё
// время, дальше регистрация. Считается по cookie tk_device в ai_usage без user_id.
export const GUEST_PHOTO_LIMIT = 3
// На одно устройство (браузер) — не больше двух аккаунтов-лимитов: семья за
// одним компьютером не пострадает, а десяток аккаунтов не даст десять лимитов.
export const DEVICE_LIMIT_FACTOR = 2

// limit === null — безлимитный доступ (активная подписка), used не считаем.
export type AiUsage = { used: number; limit: number | null }

// «Своя ситуация»: длина описания от ученика (символов). Хватает на 2–3
// предложения; длиннее — модель начинает додумывать лишнее.
export const CUSTOM_SCENE_PROMPT_MAX = 300
